// JAPAN WEB CLINIC (Dawn) — top ② the arrival check (sections/jwc-arrival.liquid; plan v3 §3G・§8-2・§8-4).
// Moved from the mock (mocks-dawn/src/mock.js setupArrival / renderArrival / renderArrivalBoxes):
// - The prefecture comes from the select or from the map (snippets/jwc-japan-map.liquid). A prefecture on the map (one
//   <g> around its hexagons, F-58) carries the select's value in data-map-pref; choosing either one updates the other,
//   and the chosen prefecture gets is-chosen (DF-16). The map is one tab stop: the arrow keys move between prefectures,
//   Enter / Space chooses.
// - The return date is today or later in Japan time.
// - The answer is the shared rule J.arrival (assets/jwc-core.js; the same as the receiving step and the cart) with the
//   schedule of Theme settings → JWC (J.settings, read by assets/jwc-common.js), written into
//   snippets/jwc-arrival-result.liquid. When it is in time, the countdown (jwc-common.js) and the button show.
// - The two inputs are kept in sessionStorage "jwc-eta" as { pref, ret }; the receiving step starts from them
//   (assets/jwc-receive.js open()). A return date in the past is not kept.
(() => {
  'use strict';
  const J = window.JWC;
  if (!J || !window.customElements || customElements.get('jwc-arrival')) return;
  const KEY = 'jwc-eta';
  const ANSWER = ['headline', 'when', 'deadline', 'before', 'osaka'];
  const MOVES = { ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowDown: [0, 1], ArrowUp: [0, -1] };

  function readEta() {
    try {
      const eta = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      return eta && typeof eta === 'object' ? eta : null;
    } catch (e) {
      return null;
    }
  }
  function keepEta(eta) {
    try {
      if (eta.pref || eta.ret) sessionStorage.setItem(KEY, JSON.stringify(eta));
      else sessionStorage.removeItem(KEY);
    } catch (e) {
      /* storage blocked: the answer still shows; the receiving step starts empty */
    }
  }
  // Theme settings → JWC: jwc-common.js reads them into J.settings (read them here if it has not run)
  function schedule() {
    let s = J.settings;
    if (!s) {
      try {
        s = JSON.parse(document.getElementById('JwcSettings')?.textContent || '{}');
      } catch (e) {
        s = {};
      }
    }
    return J.makeSchedule(s.schedule || {});
  }

  class JwcArrival extends HTMLElement {
    connectedCallback() {
      if (!this.started) this.start();
      if (!this.started) return;
      // the words "今日 / 明日" and the cutoff move with the clock: check again every minute and on coming back
      clearInterval(this.timer);
      this.timer = setInterval(() => this.update(false), 60000);
      document.addEventListener('visibilitychange', this.onVisible);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    start() {
      this.pref = this.querySelector('[data-arrival-pref]');
      this.ret = this.querySelector('[data-arrival-ret]');
      if (!this.pref || !this.ret) return;
      this.started = true;
      let data = {};
      try {
        data = JSON.parse(this.querySelector('[data-jwc-arrival-data]')?.textContent || '{}');
      } catch (e) {
        data = {};
      }
      this.T = J.texts(data.T);
      this.box = this.querySelector('[data-arrival-result]');
      this.cta = this.querySelector('[data-arrival-cta]');
      this.tiles = Array.from(this.querySelectorAll('[data-map-pref]'));
      this.onVisible = () => {
        if (!document.hidden) this.update(false);
      };

      // first values: what was entered before in this tab
      const eta = readEta();
      if (eta) {
        if (this.hasOption(eta.pref)) this.pref.value = eta.pref;
        if (J.validDate(eta.ret) && eta.ret >= schedule().today) this.ret.value = eta.ret;
      }

      this.pref.addEventListener('change', () => this.update(true));
      this.ret.addEventListener('change', () => this.update(true));
      this.ret.addEventListener('input', () => this.update(true));
      const map = this.querySelector('[data-arrival-map]');
      if (map) {
        map.addEventListener('click', (e) => {
          const tile = e.target.closest?.('[data-map-pref]');
          if (tile) this.choose(tile);
        });
        map.addEventListener('keydown', (e) => this.onKey(e));
      }
      this.update(false);
    }

    hasOption(value) {
      return Boolean(value) && Array.from(this.pref.options).some((o) => o.value === value && !o.disabled);
    }

    // a prefecture on the map chooses itself in ①, as if the select had been changed
    choose(tile) {
      const value = tile.dataset.mapPref;
      if (!this.hasOption(value)) return;
      this.stop(tile);
      if (this.pref.value === value) return;
      this.pref.value = value;
      this.pref.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // the map's one tab stop: the chosen prefecture, or the one the arrow keys moved to
    stop(tile) {
      this.tiles.forEach((t) => t.setAttribute('tabindex', t === tile ? '0' : '-1'));
    }

    onKey(e) {
      const tile = e.target.closest?.('[data-map-pref]');
      if (!tile) return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        this.choose(tile);
        return;
      }
      let next;
      if (MOVES[e.key]) next = this.nearest(tile, MOVES[e.key]);
      else if (e.key === 'Home') next = this.tiles[0];
      else if (e.key === 'End') next = this.tiles[this.tiles.length - 1];
      else return;
      e.preventDefault();
      if (!next) return;
      this.stop(next);
      next.focus();
    }

    // the nearest prefecture in the arrow's direction (within 45°), from the middle of its hexagons (data-map-x / -y)
    nearest(from, [dx, dy]) {
      const at = (t) => [Number(t.dataset.mapX) || 0, Number(t.dataset.mapY) || 0];
      const [x, y] = at(from);
      let best = null;
      let bestScore = Infinity;
      this.tiles.forEach((t) => {
        if (t === from) return;
        const [tx, ty] = at(t);
        const along = (tx - x) * dx + (ty - y) * dy;
        const across = Math.abs((tx - x) * dy) + Math.abs((ty - y) * dx);
        if (along <= 0 || across > along) return;
        const score = along + 2 * across;
        if (score < bestScore) {
          bestScore = score;
          best = t;
        }
      });
      return best;
    }

    update(save) {
      const sch = schedule();
      if (this.ret.min !== sch.today) this.ret.min = sch.today;
      const pref = this.pref.value;
      const ret = this.ret.value;
      let chosen = null;
      this.tiles.forEach((t) => {
        const on = Boolean(pref) && t.dataset.mapPref === pref;
        if (on) chosen = t;
        t.classList.toggle('is-chosen', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (chosen && !this.tiles.includes(document.activeElement)) this.stop(chosen);
      this.render(J.arrival(sch, pref, ret, this.T));
      if (save) keepEta({ pref, ret: J.validDate(ret) && ret >= sch.today ? ret : '' });
    }

    render(a) {
      const key = JSON.stringify(a);
      if (key === this.shown) return;
      this.shown = key;
      const box = this.box;
      if (box) {
        box.hidden = a.state === 'empty';
        // a soft answer (the earliest day, Osaka for sooner) without the ✕ (owner 2026-10-22)
        const look = a.soft ? 'info' : a.state;
        box.dataset.state = look;
        box.classList.toggle('is-tight', Boolean(a.tight));
        box.querySelectorAll('[data-eta-icon]').forEach((el) => (el.hidden = el.dataset.etaIcon !== look));
        ANSWER.forEach((k) => {
          const el = box.querySelector(`[data-a="${k}"]`);
          if (!el) return;
          el.textContent = a[k] || '';
          el.hidden = !a[k];
        });
        const guarantee = box.querySelector('[data-a="guarantee"]');
        if (guarantee) guarantee.hidden = !(a.state === 'ok' && a.guarantee);
      }
      if (this.cta) this.cta.hidden = a.state !== 'ok';
    }
  }

  customElements.define('jwc-arrival', JwcArrival);
})();
